import {Component, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges} from '@angular/core';
import {FormControl} from '@angular/forms';
import {DomSanitizer} from '@angular/platform-browser';
import {Router} from '@angular/router';
import config from 'devextreme/core/config';
import {alert} from 'devextreme/ui/dialog';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import {BehaviorSubject, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {Physician} from 'src/app/modules/shared/models/order-entry/physician';
import {DOMService, PWADetectorService, ScreenService} from '../../../../../../shared/services/general';
import {PatientSearchWithoutCollections} from '../../../../../shared/models/order-entry/patient';
import {OrdersService} from '../../../../../shared/services/orders.service';
import {Order, OrderStatus} from '../../../models/order';

@Component({
  selector: 'app-drawer-order',
  templateUrl: './drawer-order.component.html',
  styleUrls: ['./drawer-order.component.scss'],
})
export class DrawerOrderComponent implements OnChanges, OnDestroy {
  @Input() public orderId = -1;
  @Input() public closeEvent: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  @Input() public abnormalLabel = '';
  public reportCommentFormControl = new FormControl();
  public isLoading = false;
  public isPhoneOrTablet: boolean = false;
  public isPhone: boolean = false;
  public clinicalResultsVisible = false;
  public toxicologyResultsVisible = false;
  public microbiologyResultsVisible = false;
  public pathologyResultsVisible = false;
  public hasToxicologyUnexpectedPositive = false;
  public selectedTab = -1;
  private onDestroy$: Subject<void> = new Subject<void>();
  public order = new Order();

  constructor(
    public ordersService: OrdersService,
    private sanitizer: DomSanitizer,
    private screen: ScreenService,
    private domService: DOMService,
    private renderer: Renderer2,
    private router: Router,
    private pwaDetectorService: PWADetectorService,
  ) {
  }

  public sendToPreview() {
    if (this.pwaDetectorService.isPWA()) {
      this.router.navigate(['/secured/results/order', {id: this.orderId}]).then();
    } else {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/secured/results/order', {id: this.orderId}]),
      );
      window.open(url, '_blank');
    }
  }

  private responsiveValues() {
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.isPhone = this.screen.sizes['screen-x-small'];

    const shader = this.domService.getElementsByClassname('dx-overlay-shader').item(0) as HTMLElement;
    if (shader) {
      const button = shader.querySelector('.dx-fa-button-icon-close') as HTMLElement;
      if (button) {
        button.click();
      }
    }

    const offset = this.isPhone ? '-15 75' : '-25 75';
    config({
      floatingActionButtonConfig: {
        icon: 'rowfield',
        shading: true,
        direction: 'down',
        position: {
          of: 'body',
          my: 'right top',
          at: 'right top',
          offset,
        },
      },
    });
    repaintFloatingActionButton();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.orderId && changes.orderId.currentValue !== changes.orderId.previousValue) {
      const id = changes.orderId.currentValue;
      if (id !== -1) {
        this.isLoading = true;
        this.selectedTab = -1;
        this.getResults(id);
        this.loadOrder(id);
        this.responsiveValues();
      }
    }
  }

  public scrollTo($element: any) {
    $element.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
  }

  private getResults(orderId: number) {
    this.ordersService
      .getOrderTestsTypes(orderId)
      .pipe(take(1))
      .subscribe(response => {
        this.clinicalResultsVisible = response.hasClinicalResult;
        this.microbiologyResultsVisible = response.hasMicrobiologyResult;
        this.toxicologyResultsVisible = response.hasToxicologyResult;
        this.pathologyResultsVisible = response.hasPathologyResult;
        if (!this.clinicalResultsVisible && !this.microbiologyResultsVisible && !this.toxicologyResultsVisible && !this.pathologyResultsVisible) {
          alert('There is no result to display.', 'No results').then();
        } else {
          this.selectedTab = this.clinicalResultsVisible
            ? 0
            : this.toxicologyResultsVisible
              ? 2
              : this.microbiologyResultsVisible
                ? 1
                : this.pathologyResultsVisible
                  ? 3
                  : -1;
        }
      });
  }

  public get IsCancelled() {
    return this.order ? this.order.status === OrderStatus.Canceled : false;
  }

  public get CancellationReasons() {
    if (!this.order) {
      return '';
    }
    if (!this.order.cancellationReasons) {
      return '';
    }
    return this.order ? this.order.cancellationReasons.map(c => c.name).join(', ') : '';
  }

  public onToolbarReady(e: any) {
    if (this.isPhone) {
      const items = e.element.querySelector('.dx-toolbar-before').children as HTMLCollection[];
      if (items[1]) {
        this.renderer.setStyle(items[1], 'vertical-align', 'baseline');
        this.renderer.setStyle(items[1], 'padding-top', '25px');
      }
    }
  }

  public hasToxicologyUnexpectedPositiveResults(hasResults: boolean) {
    this.hasToxicologyUnexpectedPositive = hasResults;
  }

  public get Status() {
    let color;
    let status = OrderStatus[OrderStatus.None];
    switch (this.order.status) {
      case OrderStatus.Canceled:
        color = 'ocl-status-black';
        status = OrderStatus[OrderStatus.Canceled];
        break;
      case OrderStatus.Final:
        color = 'ocl-status-green';
        status = OrderStatus[OrderStatus.Final];
        break;
      case OrderStatus.Partial:
      case OrderStatus.PartiallyReleased:
      case OrderStatus.Preliminary:
        color = 'ocl-status-orange';
        status = OrderStatus[OrderStatus.Partial];
        break;
      case OrderStatus.Pending:
      case OrderStatus.Unreleased:
        color = 'ocl-status-yellow';
        status = OrderStatus[OrderStatus.Pending];
        break;
    }

    let orderAlert = `<div fxLayout="column" fxLayoutAlign="center">
                        <div style="width: 100px;height:15px!important;line-height: .45 !important" class="${color}">
                        ${status.toUpperCase()}</div>`;

    if (this.order.hasAlert) {
      if (this.order.hasToxicologyUnexpectedPositive) {
        orderAlert += `<div style="width: 100px;height:15px!important;line-height: .45 !important" class="ocl-status-critical">
                    ${this.abnormalLabel !== undefined && this.abnormalLabel !== null && this.abnormalLabel !== '' ?
          this.abnormalLabel.toUpperCase() :
          this.order.alert?.toUpperCase()}</div>`;
      }
      if (this.order.alert?.toUpperCase() === 'CRITICAL') {
        orderAlert += `<div style="width: 100px;height:15px!important;line-height: .45 !important" class="ocl-status-critical">${this.order.alert}</div>`;
      }
    }
    orderAlert += `</div>`;
    return this.sanitizer.bypassSecurityTrustHtml(orderAlert);
  }

  public loadOrder(orderId: number) {
    this.ordersService.getOder(orderId)
      .pipe(take(1))
      .subscribe((order: Order) => {
        this.isLoading = false;
        this.order = order;
        this.reportCommentFormControl.setValue(this.order.reportComment);
      });
  }

  public get OrderPatient() {
    return this.order.patient ? this.order.patient : new PatientSearchWithoutCollections();
  }

  public get OrderPhysician() {
    return this.order.physician ? this.order.physician : new Physician();
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

}
